import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import LandingFooter from '../components/landing/LandingFooter';
import AccessRequestModal from '../components/AccessRequestModal';
import CompareTable from '../components/pricing/CompareTable';
import CompareStack from '../components/pricing/CompareStack';
import CompareCta from '../components/pricing/CompareCta';
import { COMPARE_GROUPS } from '../components/pricing/compareData';

const PricingLayers = () => {
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  // Signed-in-user tier pricing. Fetched once on mount if a session exists.
  // Signed-out visitors keep pricing === null and see the page price-silent,
  // exactly as before Session 281.
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/pricing/for-me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.status === 200 ? r.json() : null))
      .then((data) => setPricing(data))
      .catch(() => setPricing(null));
  }, []);

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100" data-testid="pricing-layers-page">
      <SEO
        title="Pricing Layers"
        description="Compare Continuity System and Portfolio Clarity side by side. Two tiers. Both encrypted. Both yours."
        path="/pricing-layers"
      />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <Link to="/" className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <section className="text-center mb-10 sm:mb-14">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-900 mb-4">
            Two tiers. Both encrypted. Both yours.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Continuity System is the entry door &mdash; a private record and a quiet safety net. Portfolio Clarity adds the full analytical view. Start where you are; move up any time.
          </p>
        </section>

        <section className="mb-12" data-testid="compare-section">
          <CompareTable groups={COMPARE_GROUPS} />
          <CompareStack groups={COMPARE_GROUPS} />
        </section>

        <section className="mb-12 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="compare-quick-answer">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Before you decide</p>
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900 mb-3">
            What&rsquo;s the difference between Continuity System and Portfolio Clarity?
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Continuity System is the entry door &mdash; a private record of what you own, plus a quiet safety net for the people you trust. Portfolio Clarity is the full house &mdash; it includes everything in Continuity System, plus allocation charts, performance tracking, tax-year data and portfolio sharing. You can start with Continuity System and move up whenever you&rsquo;re ready.
          </p>
        </section>

        <CompareCta onRequestInvitation={() => setShowInvitationModal(true)} pricing={pricing} />
      </main>

      <LandingFooter />

      <AccessRequestModal
        open={showInvitationModal}
        onClose={() => setShowInvitationModal(false)}
      />
    </div>
  );
};

export default PricingLayers;
